import React, { useRef, useState } from 'react';
import { STARTER_SCHEDULES } from '../services/api/TaskometerAPI';
import { buildScheduleShareURL } from '../services/scheduleShare';

/**
 * Consolidated settings surface. Replaces the old floating "tweaks" panel
 * with a better-organised set of sections: look & feel, behavior, backup,
 * and the typed-confirmation wipe (previously a one-click button).
 */
export default function SettingsPanel({
  ui,
  setUIField,
  api,
  autoSchedule,
  onShowShortcuts,
  onClose,
}) {
  const fileRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeText, setWipeText] = useState('');
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [shareCopied, setShareCopied] = useState(false);

  const scheduleTypes = api?.scheduleTypes?.list ? api.scheduleTypes.list() : [];

  const shareScheduleTypes = async () => {
    if (scheduleTypes.length === 0) return;
    const link = buildScheduleShareURL(scheduleTypes);
    if (!link) return;
    try { await navigator.clipboard.writeText(link); } catch (_) {}
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2500);
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.backup.restoreJSON(reader.result);
        setStatus('restored from backup');
      } catch (err) {
        setStatus(`restore failed: ${err.message}`);
      }
      setTimeout(() => setStatus(null), 3000);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const confirmWipe = async () => {
    if (wipeText !== 'wipe my data') {
      setStatus('phrase does not match — nothing was wiped');
      setTimeout(() => setStatus(null), 2500);
      return;
    }
    try {
      await api.backup.wipe({ confirm: 'wipe my data' });
      setStatus('wiped');
    } catch (err) {
      setStatus(`wipe failed: ${err.message}`);
    }
    setWipeText('');
    setWipeOpen(false);
    setTimeout(() => setStatus(null), 2500);
  };

  const toggleNotifications = async (on) => {
    setUIField('notifications', !!on);
    if (on && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const res = await Notification.requestPermission();
      setNotifPerm(res);
    }
  };

  return (
    <div className="tm-tweaks" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        settings
        <button
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', fontFamily: 'Inter', fontSize: 18, cursor: 'pointer', color: 'var(--ink-mute)' }}
        >×</button>
      </h4>

      <section>
        <div className="tm-mono tm-md" style={{ marginTop: 6, color: 'var(--orange)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
          behavior
        </div>
        <label>
          auto-schedule new tasks
          <input
            type="checkbox"
            checked={!!autoSchedule}
            onChange={(e) => api.settings.setAutoSchedule(e.target.checked)}
          />
        </label>
        <label>
          browser notifications at task start
          <input
            type="checkbox"
            checked={!!ui.notifications}
            onChange={(e) => toggleNotifications(e.target.checked)}
          />
        </label>
        {ui.notifications && notifPerm === 'denied' && (
          <div className="tm-mono tm-sm" style={{ color: 'var(--orange)' }}>
            browser denied permission — enable it from the site settings first.
          </div>
        )}
      </section>

      <section style={{ marginTop: 14 }}>
        <div className="tm-mono tm-md" style={{ color: 'var(--orange)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
          schedule types
        </div>
        <div className="tm-mono tm-sm" style={{ color: 'var(--ink-mute)', margin: '4px 0 8px' }}>
          tag-driven time windows. write <span style={{ color: 'var(--orange)' }}>[keyword]</span> in a todo and it lands in that window's next free slot.
        </div>

        <div className="tm-mono tm-sm" style={{ color: 'var(--ink-mute)', marginBottom: 4 }}>add a popular routine (stacks)</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {STARTER_SCHEDULES.map(s => (
            <button key={s.id} className="tm-btn tm-sm" title={s.blurb}
              onClick={() => api.scheduleTypes.applyStarter(s.id)}>+ {s.name}</button>
          ))}
        </div>

        {scheduleTypes.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {scheduleTypes.map(ty => (
              <div key={ty.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: ty.color, flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{ty.name}</span>
                <span className="tm-mono tm-sm" style={{ color: 'var(--orange)' }}>[{ty.tag}]</span>
                <span className="tm-mono tm-sm" style={{ color: 'var(--ink-mute)' }}>{ty.startTime}–{ty.endTime}</span>
                <button className="tm-btn tm-sm" style={{ marginLeft: 'auto' }}
                  onClick={() => api.scheduleTypes.remove(ty.id)}>×</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="tm-mono tm-sm" style={{ color: 'var(--ink-mute)', marginBottom: 8 }}>none yet — pick a starter above.</div>
        )}

        <button className="tm-btn tm-sm" disabled={scheduleTypes.length === 0} onClick={shareScheduleTypes}
          title="copy a link that lets someone else add these windows to their schedule">
          {shareCopied ? 'link copied ✓' : 'share schedule (copy link)'}
        </button>
      </section>

      <section style={{ marginTop: 14 }}>
        <div className="tm-mono tm-md" style={{ color: 'var(--orange)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
          help
        </div>
        <button className="tm-btn tm-sm" onClick={onShowShortcuts}>
          keyboard shortcuts
        </button>
      </section>

      <section style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed var(--rule)' }}>
        <div className="tm-mono tm-md" style={{ color: 'var(--orange)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
          backup
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          <button
            className="tm-btn tm-sm"
            onClick={() => api.backup.exportICS()}
            title="iCalendar file for Google, Apple, Outlook"
          >
            export .ics
          </button>
          <button
            className="tm-btn tm-sm"
            onClick={() => api.backup.exportJSON({ minimal: true })}
            title="schedule-only JSON (tasks, blocks, schedules, rules, overrides)"
          >
            export schedule.json
          </button>
          <button
            className="tm-btn tm-ghost tm-sm"
            onClick={() => api.backup.exportJSON({ minimal: false, pretty: true })}
            title="full dump with audit log and legacy template library"
          >
            export full.json
          </button>
          <button className="tm-btn tm-sm" onClick={() => fileRef.current?.click()}>
            restore .json
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={pick}
            style={{ display: 'none' }}
          />
        </div>
      </section>

      <section style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed var(--rule)' }}>
        <div className="tm-mono tm-md" style={{ color: 'var(--orange)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
          danger zone
        </div>
        {!wipeOpen ? (
          <button
            className="tm-btn tm-sm tm-danger"
            onClick={() => setWipeOpen(true)}
          >
            wipe all data…
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            <div className="tm-mono tm-sm" style={{ color: 'var(--ink-mute)' }}>
              type <strong>wipe my data</strong> to confirm. this removes every task, time block, schedule, rule, and override.
            </div>
            <input
              className="tm-composer-input"
              placeholder="wipe my data"
              value={wipeText}
              onChange={(e) => setWipeText(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="tm-btn tm-danger tm-sm"
                onClick={confirmWipe}
                disabled={wipeText !== 'wipe my data'}
              >
                wipe everything
              </button>
              <button
                className="tm-btn tm-sm"
                onClick={() => { setWipeOpen(false); setWipeText(''); }}
              >
                cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {status && <div className="tm-mono tm-sm" style={{ marginTop: 10, color: 'var(--orange)' }}>{status}</div>}
    </div>
  );
}
